"use client";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import MuiLink from "@mui/material/Link";
import IconButton from "@mui/material/IconButton";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Container from "@mui/material/Container";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import {
  COMEDYUO_EMAIL,
  COMEDYUO_INSTAGRAM_URL,
  COMEDYUO_YOUTUBE_URL,
} from "../_utils/constants";
import { trackWorkWithUsClick } from "../_utils/workWithUsAnalytics";

const footerFont = '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

const iconStyle = {
  width: 44,
  height: 44,
  borderRadius: "50%",
  border: "1px solid rgba(255,255,255,0.5)",
  color: "#fff",
  backgroundColor: "transparent",
  "&:hover": {
    backgroundColor: "rgba(255,255,255,0.1)",
    borderColor: "rgba(255,255,255,0.8)",
  },
};

type FooterProps = {
  variant?: "default" | "dark";
};

export default function Footer({ variant: _variant = "default" }: FooterProps) {
  const { pathname } = useLocation();
  const isAboutPage = pathname === "/about";

  return (
    <Box
      component="footer"
      sx={{
        position: "relative",
        zIndex: 1,
        backgroundColor: "#0d0d0f",
        color: "#fff",
        fontFamily: footerFont,
      }}
    >
      {isAboutPage && (
        <Box
          sx={{
            py: { xs: 5, md: 6 },
            px: 2,
            textAlign: "center",
          }}
        >
          <Container maxWidth="md">
            <Typography
              sx={{
                fontFamily: footerFont,
                fontWeight: 700,
                fontSize: { xs: "1.75rem", md: "2rem" },
                color: "#fff",
                mb: 2,
              }}
            >
              Our Mission
            </Typography>
            <Typography
              sx={{
                fontFamily: footerFont,
                fontSize: { xs: "1.0625rem", md: "1.125rem" },
                lineHeight: 1.9,
                color: "rgba(255,255,255,0.9)",
                mb: 2,
              }}
            >
              We believe comedy shouldn&apos;t be confined to basements and backrooms. That&apos;s why we transform NYC&apos;s most iconic spaces, including delis, retail stores, landmarks, and more, into unforgettable comedy venues.
            </Typography>
            <Typography
              sx={{
                fontFamily: footerFont,
                fontSize: { xs: "1.0625rem", md: "1.125rem" },
                lineHeight: 1.9,
                color: "rgba(255,255,255,0.9)",
              }}
            >
              Lineups are kept secret until the comics come to the stage, so you never know who will show up. That&apos;s the magic of ComedyUO.
            </Typography>
          </Container>
        </Box>
      )}

      {isAboutPage && (
        <Box
          sx={{
            py: { xs: 5, md: 6 },
            px: 2,
            textAlign: "center",
          }}
        >
          <Container maxWidth="lg">
            <Typography
              sx={{
                fontFamily: footerFont,
                fontWeight: 700,
                fontSize: { xs: "1.75rem", md: "2rem" },
                color: "#fff",
                mb: 4,
              }}
            >
              Meet the Team
            </Typography>
            <Grid container spacing={4} justifyContent="center">
              {[
                {
                  name: "David Levine",
                  title: "Co-Founder",
                  email: "david@comedyuo.com",
                  src: "https://storage.googleapis.com/cuo-website/David+Headshot.png",
                },
                {
                  name: "Ethan Mansoor",
                  title: "Co-Founder",
                  email: "ethan@comedyuo.com",
                  src: "https://storage.googleapis.com/cuo-website/Ethan+Headshot.png",
                },
                {
                  name: "Jake Delforte",
                  title: "Operations",
                  email: "jake@comedyuo.com",
                  src: "https://storage.googleapis.com/cuo-website/Jake+Headshot.jpeg",
                },
                {
                  name: "Mike Zbyszewski",
                  title: "Sales & Partnerships",
                  email: "mike@comedyuo.com",
                  src: "https://storage.googleapis.com/cuo-website/Mike+Headshot+Private+Comedy+Show.jpg",
                },
              ].map((member) => (
                <Grid item xs={12} sm={6} md={3} key={member.name}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 160,
                        height: 160,
                        borderRadius: "50%",
                        overflow: "hidden",
                        mb: 2,
                        border: "3px solid rgba(255,255,255,0.2)",
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        component="img"
                        src={member.src}
                        alt={member.name}
                        sx={{
                          width: 160,
                          height: 160,
                          borderRadius: "50%",
                          objectFit: "cover",
                          ...(member.name === "Jake Delforte" ? { transform: "scale(1.35)" } : {}),
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: footerFont,
                        fontWeight: 600,
                        fontSize: "1.125rem",
                        color: "#fff",
                        mb: 0.5,
                      }}
                    >
                      {member.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: footerFont,
                        fontSize: "0.9rem",
                        color: "rgba(255,255,255,0.85)",
                        mb: 0.5,
                      }}
                    >
                      {member.title}
                    </Typography>
                    <MuiLink
                      href={`mailto:${member.email}`}
                      underline="hover"
                      sx={{
                        fontFamily: footerFont,
                        fontSize: "0.875rem",
                        color: "rgba(255,255,255,0.8)",
                        "&:hover": { color: "#fff" },
                      }}
                    >
                      {member.email}
                    </MuiLink>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Divider */}
      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.12)", width: "100%" }} />

      {/* Three-column footer */}
      <Container maxWidth="lg" sx={{ py: 4, px: { xs: 2, md: 3 } }}>
        <Grid container spacing={4} justifyContent="space-between">
          {/* Left: Branding */}
          <Grid item xs={12} md={5}>
            <Typography
              sx={{
                fontFamily: footerFont,
                fontWeight: 700,
                fontSize: "1.25rem",
                color: "#fff",
                mb: 1.5,
                letterSpacing: "0.02em",
              }}
            >
              COMEDYUO
            </Typography>
            <Typography
              sx={{
                fontFamily: footerFont,
                fontSize: "0.9rem",
                lineHeight: 1.6,
                color: "rgba(255,255,255,0.85)",
                mb: 2,
                maxWidth: 320,
              }}
            >
              New York City&apos;s premier live events company producing comedy and magic shows in the city&apos;s most unexpected spaces.
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <IconButton
                href={COMEDYUO_INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={iconStyle}
              >
                <InstagramIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton
                href={COMEDYUO_YOUTUBE_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={iconStyle}
              >
                <YouTubeIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <IconButton href={`mailto:${COMEDYUO_EMAIL}`} sx={iconStyle}>
                <EmailOutlinedIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Box>
          </Grid>

          {/* Middle: Navigation */}
          <Grid item xs={12} sm={4} md={2}>
            <Typography
              sx={{
                fontFamily: footerFont,
                fontWeight: 700,
                fontSize: "0.75rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.9)",
                mb: 1.5,
              }}
            >
              Navigation
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <MuiLink
                component={RouterLink}
                to="/about"
                underline="none"
                sx={{ fontFamily: footerFont, fontSize: "0.9rem", color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}
              >
                About
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/press"
                underline="none"
                sx={{ fontFamily: footerFont, fontSize: "0.9rem", color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}
              >
                Press
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/newsletter"
                underline="none"
                sx={{ fontFamily: footerFont, fontSize: "0.9rem", color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}
              >
                Newsletter
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/faq"
                underline="none"
                sx={{ fontFamily: footerFont, fontSize: "0.9rem", color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}
              >
                FAQ
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/private-events"
                underline="none"
                sx={{ fontFamily: footerFont, fontSize: "0.9rem", color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}
              >
                Contact
              </MuiLink>
            </Box>
          </Grid>

          {/* Right: Get Involved */}
          <Grid item xs={12} sm={4} md={2}>
            <Typography
              sx={{
                fontFamily: footerFont,
                fontWeight: 700,
                fontSize: "0.75rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.9)",
                mb: 1.5,
              }}
            >
              Get Involved
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
              <MuiLink
                component={RouterLink}
                to="/tickets"
                underline="none"
                sx={{ fontFamily: footerFont, fontSize: "0.9rem", color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}
              >
                Get Tickets
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/work-with-us"
                onClick={trackWorkWithUsClick}
                underline="none"
                sx={{ fontFamily: footerFont, fontSize: "0.9rem", color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}
              >
                Work With Us
              </MuiLink>
              <MuiLink
                component={RouterLink}
                to="/private-events"
                underline="none"
                sx={{ fontFamily: footerFont, fontSize: "0.9rem", color: "rgba(255,255,255,0.85)", "&:hover": { color: "#fff" } }}
              >
                Private Events
              </MuiLink>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Bottom divider */}
      <Box sx={{ borderTop: "1px solid rgba(255,255,255,0.12)", width: "100%" }} />

      {/* Copyright row */}
      <Box
        sx={{
          py: 2,
          px: { xs: 2, md: 3 },
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: footerFont,
            fontSize: "0.8rem",
            color: "rgba(255,255,255,0.7)",
          }}
        >
          © 2026 ComedyUO. All rights reserved.
        </Typography>
        <Typography
          sx={{
            fontFamily: footerFont,
            fontSize: "0.8rem",
            color: "rgba(255,255,255,0.7)",
          }}
        >
          Made with ❤️ in NYC
        </Typography>
      </Box>
    </Box>
  );
}
